import {
  childNumberValue,
  childsAtHomeValue,
  EMemberChoicesLabels,
  IMemberChoices,
  importanceValue,
  profileSmokeHabbitValue,
  searchSmokeHabitValue,
} from 'affiny-core/modules/api/models/memberchoice/memberchoice.types';
import { IAnswer } from 'affiny-core/modules/api/models/reflist';
import { getAnswersByLabel } from 'affiny-core/modules/helpers/reflist';
import {
  useMemberAttribute,
  useSelectMyProfileMemberChoices,
  useSelectOtherProfileMemberChoices,
  useSelectQuestionsForKeys,
} from './useGlobal';

export const useCommonCriteriaSelector = (
  otherAccountId: string,
  i18nTranslator: (key: string, options?: object) => string
) => {
  const allPossibleImportantCriteria: EMemberChoicesLabels[] = [
    // need to  add distance at some point ( <10km, <30km, <150km )
    EMemberChoicesLabels.searchAge,
    EMemberChoicesLabels.searchHeight,
    EMemberChoicesLabels.searchStudies,
    EMemberChoicesLabels.searchSmokeHabit,
    EMemberChoicesLabels.childsAtHome,
    EMemberChoicesLabels.searchEthnicity,
    EMemberChoicesLabels.searchReligion,
  ];

  const filterQuestionsLabels: EMemberChoicesLabels[] = [
    // used for answers label
    // need to  add distance at some point ( <10km, <30km, <150km )
    EMemberChoicesLabels.searchAge,
    EMemberChoicesLabels.searchHeight,
    EMemberChoicesLabels.profileStudies,
    EMemberChoicesLabels.profileSmokeHabit,
    EMemberChoicesLabels.childNumber,
    EMemberChoicesLabels.profileEthnicity,
    EMemberChoicesLabels.profileReligion,
  ];

  const fulfilledImportantCriteriaValues: string[] = []; // displayed values in Discover card (for tags) -- is the output of this hook

  const otherAge = useMemberAttribute('age', otherAccountId);

  const filterQuestions = useSelectQuestionsForKeys(filterQuestionsLabels); // all the questions to filter out selection

  const myCriteria = useSelectMyProfileMemberChoices(); // to know my responses to all PC questions

  const otherCriteria = useSelectOtherProfileMemberChoices(otherAccountId); // to know other responses to all PC questions

  const getAnswerLabelOfOtherProfileForQuestion = (
    questionLabel: EMemberChoicesLabels
  ) => {
    const possibleAnswers = getAnswersByLabel(filterQuestions, questionLabel);
    const answers = possibleAnswers.answers as IAnswer[];

    let label = '';

    if (otherCriteria[questionLabel]) {
      label = answers.reduce((acc, currentValue) => {
        // retrieve the actual answer of the question
        if (
          (otherCriteria[questionLabel] as IMemberChoices)[0] ===
          currentValue.id
        ) {
          acc = currentValue.label;
        }

        return acc;
      }, '');
    }

    return label ? label : null;
  };

  const displayedTagFromFullfilledCriteria = (
    criteria: EMemberChoicesLabels
  ) => {
    let result: string | null = null;
    let idx = -1;

    switch (criteria) {
      case EMemberChoicesLabels.searchAge:
        result = i18nTranslator('common.age', { age: otherAge });
        break;
      case EMemberChoicesLabels.searchHeight:
        result = otherCriteria.profileHeight
          ? `${otherCriteria.profileHeight[0]} ${i18nTranslator(
              'profileCapture.units.height'
            )}`
          : null;
        break;
      case EMemberChoicesLabels.searchStudies:
        result = getAnswerLabelOfOtherProfileForQuestion(
          EMemberChoicesLabels.profileStudies
        );
        break;
      case EMemberChoicesLabels.searchSmokeHabit:
        idx =
          otherCriteria && otherCriteria.profileSmokeHabit
            ? otherCriteria.profileSmokeHabit[0]
            : -1;

        if (idx !== -1) {
          result = i18nTranslator(
            `profile.informations.response.profileSmokeHabit.${idx}`
          );
        }
        break;
      case EMemberChoicesLabels.childsAtHome:
        idx =
          otherCriteria && otherCriteria.childNumber
            ? otherCriteria.childNumber[0]
            : -1;

        if (idx !== -1) {
          result = i18nTranslator(
            `profile.informations.response.childNumber.${idx}`
          );
        }
        break;
      case EMemberChoicesLabels.searchEthnicity:
        result = getAnswerLabelOfOtherProfileForQuestion(
          EMemberChoicesLabels.profileEthnicity
        );
        break;
      case EMemberChoicesLabels.searchReligion:
        result = getAnswerLabelOfOtherProfileForQuestion(
          EMemberChoicesLabels.profileReligion
        );
        break;
    }

    return result;
  };

  allPossibleImportantCriteria.forEach(criteria => {
    switch (criteria) {
      case EMemberChoicesLabels.searchAge: // regarding age
        // age isnt in profile criteria, need to retrieve it with MemberAttribute...
        if (
          myCriteria.searchAge &&
          (myCriteria.searchAge.min > 18 || myCriteria.searchAge.max < 99) && // age is important to me
          myCriteria.searchAge.min < otherAge &&
          myCriteria.searchAge.max > otherAge // other profile age matches my searched age
        ) {
          const searchAge = displayedTagFromFullfilledCriteria(
            EMemberChoicesLabels.searchAge
          );

          if (searchAge) {
            fulfilledImportantCriteriaValues.push(searchAge);
          }
        }
        break;
      case EMemberChoicesLabels.searchHeight: // regarding height
        if (
          myCriteria.searchHeight &&
          (myCriteria.searchHeight.min > 139 ||
            myCriteria.searchHeight.max < 190) && // height is important to me
          otherCriteria.profileHeight &&
          otherCriteria.profileHeight[0] > myCriteria.searchHeight.min &&
          otherCriteria.profileHeight[0] < myCriteria.searchHeight.max // other profile height matches my searched height
        ) {
          const searchHeight = displayedTagFromFullfilledCriteria(
            EMemberChoicesLabels.searchHeight
          );

          if (searchHeight) {
            fulfilledImportantCriteriaValues.push(searchHeight);
          }
        }
        break;
      case EMemberChoicesLabels.searchStudies: // regarding level of studies
        if (
          myCriteria.searchStudies &&
          myCriteria.searchStudiesWeighting &&
          myCriteria.searchStudiesWeighting[0] >= importanceValue.important && // 1 for "not important", 2 for important, 3 for crucial
          otherCriteria.profileStudies &&
          otherCriteria.profileStudies[0] >= myCriteria.searchStudies[0] // other profile studies match my searchStudies
        ) {
          const searchStudies = displayedTagFromFullfilledCriteria(
            EMemberChoicesLabels.searchStudies
          );

          if (searchStudies) {
            fulfilledImportantCriteriaValues.push(searchStudies);
          }
        }
        break;
      case EMemberChoicesLabels.searchSmokeHabit:
        if (
          otherCriteria.profileSmokeHabit &&
          myCriteria.searchSmokeHabit &&
          myCriteria.searchSmokeHabitWeighting &&
          myCriteria.searchSmokeHabitWeighting[0] >= importanceValue.important // smoke (whether smoking or not smoking) is important or very important to me
        ) {
          if (
            (myCriteria.searchSmokeHabit[0] ===
              searchSmokeHabitValue.notSmoking &&
              otherCriteria.profileSmokeHabit[0] <=
                profileSmokeHabbitValue.sometimes) ||
            (myCriteria.searchSmokeHabit[0] === searchSmokeHabitValue.smoking &&
              otherCriteria.profileSmokeHabit[0] >=
                profileSmokeHabbitValue.sometimes)
          ) {
            const searchSmokeHabit = displayedTagFromFullfilledCriteria(
              EMemberChoicesLabels.searchSmokeHabit
            );

            if (searchSmokeHabit) {
              fulfilledImportantCriteriaValues.push(searchSmokeHabit);
            }
          }
        }
        break;
      case EMemberChoicesLabels.childsAtHome: // 1 for willing to have children at home, 0 for no child at home
        if (
          myCriteria.childsAtHome &&
          otherCriteria.childNumber &&
          myCriteria.childsAtHomeWeighting &&
          myCriteria.childsAtHomeWeighting[0] >= importanceValue.important // child at home, whether none or some, is important or very important to me
        ) {
          if (
            (myCriteria.childsAtHome[0] === childsAtHomeValue.yes &&
              otherCriteria.childNumber[0] >= childNumberValue.one) || // s-he has children and i also wish s-he has
            ((myCriteria.childsAtHome[0] === childsAtHomeValue.no ||
              myCriteria.childsAtHome[0] === childsAtHomeValue.no_legacy) &&
              otherCriteria.childNumber[0] <= childNumberValue.none) // s-he doesnt have child and i dont want any
          ) {
            const childsAtHome = displayedTagFromFullfilledCriteria(
              EMemberChoicesLabels.childsAtHome
            );

            if (childsAtHome) {
              fulfilledImportantCriteriaValues.push(childsAtHome);
            }
          }
        }
        break;
      case EMemberChoicesLabels.searchEthnicity: // from 1 to 7: European, African, Arabic, Asian, Hispanic, Other, Indian (no comment on Indian coming after Other... WT*)
        if (
          myCriteria.searchEthnicity &&
          otherCriteria.profileEthnicity &&
          myCriteria.searchEthnicityWeighting &&
          myCriteria.searchEthnicityWeighting[0] >= importanceValue.important // ethnicity is important or very important to me
        ) {
          if (
            myCriteria.searchEthnicity.indexOf(
              otherCriteria.profileEthnicity[0]
            ) >= 0 // his-her ethnicity fits my search
          ) {
            const searchEthnicity = displayedTagFromFullfilledCriteria(
              EMemberChoicesLabels.searchEthnicity
            );

            if (searchEthnicity) {
              fulfilledImportantCriteriaValues.push(searchEthnicity);
            }
          }
        }
        break;
      case EMemberChoicesLabels.searchReligion: // 11,12,13,18,19,20,21,22,24 : Athee, agnostic, other, catholic, protestant, israelit, muslim, buddhist, orthodox
        if (
          myCriteria.searchReligion &&
          otherCriteria.profileReligion &&
          myCriteria.searchReligionWeighting &&
          myCriteria.searchReligionWeighting[0] >= importanceValue.important // religion is important or very important to me
        ) {
          if (
            myCriteria.searchReligion.indexOf(
              otherCriteria.profileReligion[0]
            ) >= 0 // his-her religion fits my search
          ) {
            const searchReligion = displayedTagFromFullfilledCriteria(
              EMemberChoicesLabels.searchReligion
            );

            if (searchReligion) {
              fulfilledImportantCriteriaValues.push(searchReligion);
            }
          }
        }
        break;
      default:
        // sentry should trigger an error!
        break;
    }
  });

  return fulfilledImportantCriteriaValues;
};
